import { Modal } from 'antd';
import { ModalFuncProps } from 'antd/es/modal';
import { t } from 'helpers/i18n';
import { FirebaseOptions, initializeApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  MessagePayload,
  onMessage,
} from 'firebase/messaging';
import {
  serviceWorkerConstants,
  unsConstants,
  trackingConstants,
} from 'constants/index';
import { unsServices, userServices } from 'services';
import { INotificationConfig } from 'interfaces';
import trackingHelpers from './tracking';

const { COMMAND_INIT_FIREBASE } = serviceWorkerConstants;
const {
  FCM_TOKEN,
  UNS_APP_ID,
  FCM_USER_ID,
  FCM_REGISTER_TOKEN_SUCCESS,
  FCM_TRANSPORT,
} = unsConstants;

const showConfirmModal = (props: ModalFuncProps) => {
  Modal.confirm({
    title: t('RequestConfirmation'),
    maskClosable: true,
    content: t('AreYouSureToContinue'),
    okText: t('Confirm'),
    cancelText: t('Cancel'),
    ...props,
  });
};

const getNotificationConfig = (
  extraConfig?: Record<string, unknown>,
  isAdminFlow?: boolean
): INotificationConfig | null => {
  if (!extraConfig) return null;

  const apiKey = (isAdminFlow
    ? extraConfig.firebaseNotificationAdminApiKey
    : extraConfig.firebaseNotificationApiKey
  )?.toString();
  const appId = (isAdminFlow
    ? extraConfig.firebaseNotificationAdminAppId
    : extraConfig.firebaseNotificationAppId
  )?.toString();
  const measurementId = (isAdminFlow
    ? extraConfig.firebaseNotificationAdminMeasurementId
    : extraConfig.firebaseNotificationMeasurementId
  )?.toString();
  if (!apiKey || !appId) return null;

  const stnAppId = isAdminFlow
    ? extraConfig.stnAdminAppId?.toString()
    : extraConfig.stnAppId?.toString();
  if (!stnAppId) return null;

  return {
    stnAppId,
    vapidKey: extraConfig.firebaseNotificationVapidKey?.toString(),
    firebaseConfigs: {
      apiKey,
      appId,
      measurementId,
      authDomain: extraConfig.firebaseNotificationAuthDomain?.toString(),
      databaseURL: extraConfig.firebaseNotificationDatabaseUrl?.toString(),
      projectId: extraConfig.firebaseNotificationProjectId?.toString(),
      storageBucket: extraConfig.firebaseNotificationStorageBucket?.toString(),
      messagingSenderId: extraConfig.firebaseNotificationMessagingSenderId?.toString(),
    },
  };
};

const trackForegroundNotificationReceived = (payload: MessagePayload) => {
  const data = payload.data || {};
  trackingHelpers.nonBlockTrackUserClickEvent(
    trackingConstants.TRACKING_CONTENT_REGION_NAME.NotificationBell,
    trackingConstants.TRACKING_CONTENT_NAME.NotificationReceived,
    JSON.stringify({
      id: data.id || payload.messageId,
      type: data.type,
      hasDeeplink: !!data.deeplink,
      source: 'foreground',
    })
  );
};

const installFirebaseAndUnsNotificationService = async (
  onReceiveNewNotification: (payload: MessagePayload) => void,
  extraConfig?: Record<string, unknown>,
  isAdminFlow?: boolean
) => {
  const serviceWorkerRegistration = await navigator.serviceWorker.ready;
  if (!serviceWorkerRegistration) {
    console.warn('Service worker is not ready');
    return;
  }

  if (!('Notification' in window)) {
    console.warn('This browser does not support desktop notification');
    return;
  }

  const notificationConfigs = getNotificationConfig(extraConfig, isAdminFlow);
  if (!notificationConfigs) {
    console.warn('Extra config is not provided');
    return;
  }

  try {
    const { stnAppId, vapidKey, firebaseConfigs } = notificationConfigs;

    // Step 1: Config firebase in service worker
    await registerFirebaseInServiceWorker(firebaseConfigs, isAdminFlow);

    // Step 2: Init firebase app
    const app = initializeApp(firebaseConfigs);
    const messaging = getMessaging(app);
    const firebaseToken = await getToken(messaging, {
      serviceWorkerRegistration,
      vapidKey,
    });

    // Step 3: Register FCM token to UNS
    await registerUNS(firebaseToken, stnAppId);

    // Step 4: Listen to new notification in foreground
    onMessage(messaging, payload => {
      trackForegroundNotificationReceived(payload);
      onReceiveNewNotification(payload);
    });
  } catch (error) {
    console.error('Error while installing notification', error);
    return;
  }
};

/**
 * `isAdminFlow` travels with the config because the service worker needs it to
 * decide whether to take over notification clicks, and this message is the only
 * moment it can learn it: the SW cannot read `window`, and a click may arrive
 * long after every tab is gone. The SW persists what it receives here.
 */
const registerFirebaseInServiceWorker = async (
  firebaseConfigs: FirebaseOptions,
  isAdminFlow?: boolean
) => {
  const serviceWorkerRegistration = await navigator.serviceWorker.ready;
  if (!serviceWorkerRegistration) {
    return;
  }

  serviceWorkerRegistration.active?.postMessage({
    command: COMMAND_INIT_FIREBASE,
    data: { firebaseConfigs, isAdminFlow: !!isAdminFlow },
  });
};

const registerUNS = async (
  fcmToken: string,
  unsAppId?: string
): Promise<void> => {
  if (!fcmToken || !unsAppId) return;

  const lsFCMToken = localStorage.getItem(FCM_TOKEN);
  const lsUNSAppId = localStorage.getItem(UNS_APP_ID);
  const lsUserId = localStorage.getItem(FCM_USER_ID);
  const lsRegisterTokenSuccess = localStorage.getItem(
    FCM_REGISTER_TOKEN_SUCCESS
  );
  const userInfo = userServices.getUserInfo();
  if (!fcmToken || !unsAppId) return;
  const shouldRegisterTokenUNS =
    // firebase updates token OR first time users using the app on browser
    fcmToken !== lsFCMToken ||
    // notificationAppId config updates
    unsAppId !== lsUNSAppId ||
    // user logs in
    userInfo.sub !== lsUserId ||
    // registration failed last time
    lsRegisterTokenSuccess !== 'true';

  if (shouldRegisterTokenUNS) {
    try {
      await unsServices.registerToken({
        app_id: unsAppId,
        user_id: userInfo.sub,
        transport: FCM_TRANSPORT,
        token: fcmToken,
        email: userInfo.email,
        phone: userInfo.phone_number,
      });
      localStorage.setItem(FCM_REGISTER_TOKEN_SUCCESS, 'true');
      localStorage.setItem(FCM_USER_ID, userInfo.sub);
    } catch (e) {
      localStorage.setItem(FCM_REGISTER_TOKEN_SUCCESS, 'false');
    }
  }
  localStorage.setItem(FCM_TOKEN, fcmToken);
  localStorage.setItem(UNS_APP_ID, unsAppId);
};

export default {
  showConfirmModal,
  installFirebaseAndUnsNotificationService,
  getNotificationConfig,
};
